import { useQuery } from 'urql';
import {
  GetSocialAnalyticsSummaryWithPreviousPeriodQuery,
  GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables,
  Segment,
  MentionSource,
} from '@frontend/gql/social/graphql';
import { useMemo } from 'react';
import { getSocialAnalyticsSummaryWithPreviousPeriod } from '../graphql';
import { getPreviousIntervalRange } from './useSummaryData';

type RequiredKeys = Exclude<
  keyof GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables,
  'projectIds' | 'groupIds' | 'segments' | 'memberIds' | 'previousStartDate' | 'previousEndDate' | 'mentionSources'
>;

export type GraphQLQueryVariables = Pick<GetSocialAnalyticsSummaryWithPreviousPeriodQueryVariables, RequiredKeys> & {
  projectIds?: number[];
  groupIds?: number[];
  segments?: Segment[];
  memberIds?: string[];
  mentionSources?: MentionSource[];
};

interface UseSummaryQueryDataProps {
  clientId: string | undefined;
  queryVariables: GraphQLQueryVariables;
  pause?: boolean;
}

export const useTrendsSummaryQuery = ({
  clientId,
  queryVariables,
  pause = false,
}: UseSummaryQueryDataProps) => {
  const previousPeriod = useMemo(() => {
    const getDateString = (date: string | Date | undefined): string => {
      if (!date) return new Date().toISOString().split('T')[0];
      return typeof date === 'string' ? date : date.toISOString().split('T')[0];
    };

    return getPreviousIntervalRange({
      startDate: getDateString(queryVariables.startDate),
      endDate: getDateString(queryVariables.endDate),
    });
  }, [queryVariables.startDate, queryVariables.endDate]);

  const [{ data, fetching, error }] = useQuery<GetSocialAnalyticsSummaryWithPreviousPeriodQuery>({
    query: getSocialAnalyticsSummaryWithPreviousPeriod,
    variables: {
      ...queryVariables,
      previousStartDate: previousPeriod.startDate,
      previousEndDate: previousPeriod.endDate,
      segments: queryVariables.segments || [],
      mentionSources: queryVariables.mentionSources || [],
      memberIds: queryVariables.memberIds || [],
    },
    pause: !clientId || pause,
  });

  return { data, fetching, error };
};
